import React, { useState } from "react";
import { useTemplateCustomisation } from "./useTemplateCustomisation";
import SlotForm from "./SlotForm";
import TemplatePreview from "../Reminders/TemplatePreview";
import "./EmailTemplates.css";

/** Template selected when the section first opens. */
const INITIAL_TEMPLATE = "data-owner-reminder";

/**
 * Email templates section. Lets an admin adjust the wording and branding of the
 * reminder emails and see the result immediately.
 *
 * Only named slots are editable — the layout and the KPI tables stay in the
 * shipped template, so a customisation survives template updates and cannot
 * break the email's structure.
 * @returns {JSX.Element}
 */
export default function EmailTemplates() {
  const [templateKey, setTemplateKey] = useState(INITIAL_TEMPLATE);
  const [previewToken, setPreviewToken] = useState(0);
  const { templates, slots, loading, error, save, reset } =
    useTemplateCustomisation(templateKey);

  /**
   * Persists the slots, then forces the preview to re-render with them.
   * @param {object} form the slot values.
   * @returns {Promise<{ok: boolean, message?: string}>}
   */
  const submit = async (form) => {
    const res = await save(form);
    if (res.ok) setPreviewToken((token) => token + 1);
    return res;
  };

  /**
   * Reverts to the shipped defaults, then refreshes the preview.
   * @returns {Promise<{ok: boolean, message?: string}>}
   */
  const revert = async () => {
    const res = await reset();
    if (res.ok) setPreviewToken((token) => token + 1);
    return res;
  };

  return (
    <div className="emt-root">
      <header className="emt-header">
        <p className="emt-muted">
          Adjust the wording and branding of your reminder emails. The summary
          tables and layout stay as they are, so your changes carry over when
          the templates are updated.
        </p>
      </header>

      <section className="emt-panel">
        <label className="emt-label" htmlFor="emt-template">
          Template
        </label>
        <select
          id="emt-template"
          className="emt-select"
          value={templateKey}
          onChange={(e) => setTemplateKey(e.target.value)}
        >
          {templates.map((t) => (
            <option key={t.key} value={t.key}>
              {t.label}
            </option>
          ))}
        </select>

        {error && (
          <div className="emt-error" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <p className="emt-muted">Loading…</p>
        ) : (
          slots && (
            <SlotForm
              key={`${templateKey}-${slots.isCustomised}`}
              slots={slots}
              onSubmit={submit}
              onReset={revert}
            />
          )
        )}
      </section>

      <section className="emt-panel">
        <TemplatePreview
          key={`${templateKey}-${previewToken}`}
          templateKey={templateKey}
        />
      </section>
    </div>
  );
}
